import React, { useMemo, useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { DocumentGroupKey, DocumentKey, FormSubmission } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';
import CarePlanModeModal from '../modals/CarePlanModeModal';
import MedicalIdLookupModal from '../modals/MedicalIdLookupModal';
import { useLocation, useNavigate } from 'react-router-dom';

// Import API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api';

type GroupOption = {
  key: DocumentGroupKey;
  docs: DocumentKey[];
};

// PDF/form display names are intentionally kept in English (client requirement).
const DOCUMENT_LABELS: Record<DocumentKey, string> = {
  consent: 'Consent',
  csConsentPartnership: 'Consent Partnership',
  ecmRoi: 'Release of Information',
  adultEcmAssessment: '(ECM) Adult Comprehensive Assessment',
  cyEcmAssessment: 'Assessment (Children)',
  housingAssessment: 'Housing Assessment',
  carePlan: 'Care Plan',
  partnershipCarePlan: 'Partnership Care Plan',
};

const GROUP_OPTIONS: GroupOption[] = [
  { key: 'contra_costa_adult', docs: ['consent', 'ecmRoi', 'adultEcmAssessment'] },
  { key: 'contra_costa_children', docs: ['consent', 'ecmRoi', 'cyEcmAssessment'] },
  { key: 'partnership_adult', docs: ['csConsentPartnership', 'ecmRoi', 'partnershipCarePlan'] },
  { key: 'partnership_children', docs: ['csConsentPartnership', 'ecmRoi', 'partnershipCarePlan'] },
];

const INDIVIDUAL_OPTIONS: Array<{ key: DocumentKey; label: string }> = [
  { key: 'carePlan', label: DOCUMENT_LABELS.carePlan },
  { key: 'partnershipCarePlan', label: DOCUMENT_LABELS.partnershipCarePlan },
  { key: 'housingAssessment', label: DOCUMENT_LABELS.housingAssessment },
  { key: 'csConsentPartnership', label: DOCUMENT_LABELS.csConsentPartnership },
  { key: 'consent', label: DOCUMENT_LABELS.consent },
  { key: 'ecmRoi', label: DOCUMENT_LABELS.ecmRoi },
  { key: 'adultEcmAssessment', label: DOCUMENT_LABELS.adultEcmAssessment },
  { key: 'cyEcmAssessment', label: DOCUMENT_LABELS.cyEcmAssessment },
];

interface Props {
  selectedDocuments: DocumentKey[];
}

const DocumentSelectionSection: React.FC<Props> = ({ selectedDocuments }) => {
  const { watch, setValue, getValues } = useFormContext<FormSubmission>();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedGroup = watch('documentSelection.selectedGroup') || '';
  const individualDocuments = watch('documentSelection.individualDocuments') || [];
  const finalSelected = watch('documentSelection.finalSelected') || [];

  // Care Plan standalone workflow state
  const [showCarePlanModeModal, setShowCarePlanModeModal] = useState(false);
  const [showMedicalIdModal, setShowMedicalIdModal] = useState(false);
  const [medicalIdLookupError, setMedicalIdLookupError] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isCarePlanPromptDismissed, setIsCarePlanPromptDismissed] = useState(false);

  // Check if we're in selective edit mode (user came from admin modal)
  const params = new URLSearchParams(location.search);
  const editSubmissionId = params.get('editSubmissionId');
  const selectedPDFsParam = params.get('selectedPDFs');
  const isSelectiveEditMode = editSubmissionId && selectedPDFsParam;

  // Check if only Care Plan is selected (triggers standalone workflow)
  const isCarePlanOnlySelected = useMemo(() => {
    if (isSelectiveEditMode) return false; // Don't trigger in edit mode
    
    const groupDocs = selectedGroup ? (GROUP_OPTIONS.find(g => g.key === selectedGroup)?.docs || []) : [];
    const allSelected = [...groupDocs, ...individualDocuments];
    return allSelected.length === 1 && allSelected.includes('carePlan');
  }, [selectedGroup, individualDocuments, isSelectiveEditMode]);

  // Show Care Plan mode modal when only Care Plan is selected
  useEffect(() => {
    if (!isCarePlanOnlySelected) {
      setIsCarePlanPromptDismissed(false);
      return;
    }

    if (isCarePlanOnlySelected && !showCarePlanModeModal && !showMedicalIdModal && !isCarePlanPromptDismissed) {
      setShowCarePlanModeModal(true);
    }
  }, [isCarePlanOnlySelected, showCarePlanModeModal, showMedicalIdModal, isCarePlanPromptDismissed]);

  const groupDocSet = useMemo(() => {
    const group = GROUP_OPTIONS.find((g) => g.key === selectedGroup);
    return new Set(group?.docs || []);
  }, [selectedGroup]);

  const handleGroupChange = (value: string) => {
    const next = (value || '') as DocumentGroupKey | '';
    setValue('documentSelection.selectedGroup', next, { shouldDirty: true });
  };

  const handleIndividualToggle = (doc: DocumentKey, checked: boolean) => {
    const current = new Set(getValues('documentSelection.individualDocuments') || []);
    if (checked) current.add(doc);
    else current.delete(doc);
    setValue('documentSelection.individualDocuments', Array.from(current), { shouldDirty: true });
  };

  const handleFinalSelectedToggle = (doc: DocumentKey, checked: boolean) => {
    const current = new Set(getValues('documentSelection.finalSelected') || []);
    if (checked) current.add(doc);
    else current.delete(doc);
    setValue('documentSelection.finalSelected', Array.from(current), { shouldDirty: true });
  };

  // Care Plan workflow handlers
  const handleCarePlanModeClose = () => {
    // Reset Care Plan selection if user closes modal without choosing
    setValue('documentSelection.individualDocuments', [], { shouldDirty: true });
    setIsCarePlanPromptDismissed(false);
    setShowCarePlanModeModal(false);
  };

  const handleAddToExisting = () => {
    setIsCarePlanPromptDismissed(true);
    setShowCarePlanModeModal(false);
    setShowMedicalIdModal(true);
    setMedicalIdLookupError('');
  };

  const handleCreateNew = () => {
    setIsCarePlanPromptDismissed(true);
    setShowCarePlanModeModal(false);
    // Continue with normal flow - user will fill out complete form
  };

  const handleMedicalIdSubmit = async (medicalId: string) => {
    setIsLookingUp(true);
    setMedicalIdLookupError('');

    try {
      // Search for existing submission by Medical ID
      const response = await fetch(
        `${API_BASE_URL}/forms/submissions/search-by-medical-id/${encodeURIComponent(medicalId)}`,
        {
          method: 'GET',
          cache: 'no-store',
        }
      );
      
      console.log('Medical ID lookup response:', response.status, response.statusText);
      
      if (response.ok) {
        const existingSubmission = await response.json();
        console.log('Found existing submission:', existingSubmission);
        
        // Pre-fill the Medical ID and mark for care plan addition
        setValue('memberInfo.medicalId', medicalId, { shouldDirty: true });
        
        // Navigate to Care Plan step with existing submission context
        const params = new URLSearchParams(location.search);
        params.set('existingSubmissionId', existingSubmission.submissionId || existingSubmission.id);
        params.set('carePlanMode', 'true');
        navigate(`?${params.toString()}`);
        
        setShowMedicalIdModal(false);
      } else if (response.status === 404) {
        setMedicalIdLookupError('No submission found with this Medical ID. Please check and try again.');
      } else {
        const errorText = await response.text();
        console.error('Medical ID lookup failed:', response.status, errorText);
        setMedicalIdLookupError(`Error searching for Medical ID (${response.status}). Please try again.`);
      }
    } catch (error) {
      console.error('Medical ID lookup error:', error);
      setMedicalIdLookupError('Network error. Please check your connection and try again.');
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleMedicalIdModalClose = () => {
    setShowMedicalIdModal(false);
    setMedicalIdLookupError('');
    // Go back to Care Plan mode selection
    setIsCarePlanPromptDismissed(false);
    setShowCarePlanModeModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Only show group/individual selection if NOT in selective edit mode */}
      {!isSelectiveEditMode && (
        <>
          <div>
        <h3 className="text-lg font-semibold text-gray-900">{t('docSelection.groupHeading')}</h3>
        <p className="text-sm text-gray-600 mt-1">
          {t('docSelection.groupSubtitle')}
        </p>
        <select
          className="mt-3 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          value={selectedGroup}
          onChange={(e) => handleGroupChange(e.target.value)}
        >
          <option value="">{t('docSelection.noGroup')}</option>
          {GROUP_OPTIONS.map((group) => (
            <option key={group.key} value={group.key}>
              {t(`docSelection.group.${group.key}`)}
            </option>
          ))}
        </select>
      </div>

      {selectedGroup && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          {t('docSelection.groupIncludes')}{' '}
          {GROUP_OPTIONS.find((g) => g.key === selectedGroup)?.docs.map((k) => DOCUMENT_LABELS[k]).join(', ')}
        </div>
      )}

      <div>
        <h3 className="text-lg font-semibold text-gray-900">{t('docSelection.individualHeading')}</h3>
        <p className="text-sm text-gray-600 mt-1">
          {t('docSelection.individualSubtitle')}
        </p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {INDIVIDUAL_OPTIONS.filter((opt) => !groupDocSet.has(opt.key)
            && !(selectedGroup.startsWith('contra_costa') && (opt.key === 'csConsentPartnership' || opt.key === 'partnershipCarePlan'))
            && !(selectedGroup.startsWith('partnership') && (opt.key === 'carePlan' || opt.key === 'adultEcmAssessment' || opt.key === 'cyEcmAssessment'))
          ).map((opt) => (
            <label key={opt.key} className="flex items-center gap-2 rounded border border-gray-200 p-2 text-sm">
              <input
                type="checkbox"
                checked={individualDocuments.includes(opt.key)}
                onChange={(e) => handleIndividualToggle(opt.key, e.target.checked)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>
        </>
      )}

      {/* Show all documents for selective edit mode, so new ones can be added */}
      {isSelectiveEditMode && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Selected Documents</h3>
          <p className="text-sm text-gray-600 mt-1">
            Check or uncheck documents to modify your selection.
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {INDIVIDUAL_OPTIONS.map((opt) => (
              <label key={opt.key} className="flex items-center gap-2 rounded border border-gray-200 p-2 text-sm">
                <input
                  type="checkbox"
                  checked={finalSelected.includes(opt.key)}
                  onChange={(e) => handleFinalSelectedToggle(opt.key, e.target.checked)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
        <div className="text-sm font-medium text-gray-900">
          {t('docSelection.finalSelected', { count: selectedDocuments.length })}
        </div>
        <div className="mt-2 text-sm text-gray-700">
          {selectedDocuments.length > 0
            ? selectedDocuments.map((k) => DOCUMENT_LABELS[k]).join(', ')
            : t('docSelection.noneSelected')}
        </div>
      </div>

      {/* Care Plan Standalone Workflow Modals */}
      <CarePlanModeModal
        isOpen={showCarePlanModeModal}
        onClose={handleCarePlanModeClose}
        onAddToExisting={handleAddToExisting}
        onCreateNew={handleCreateNew}
      />

      <MedicalIdLookupModal
        isOpen={showMedicalIdModal}
        onClose={handleMedicalIdModalClose}
        onSubmit={handleMedicalIdSubmit}
        isLoading={isLookingUp}
        error={medicalIdLookupError}
      />
    </div>
  );
};

export default DocumentSelectionSection;
